import { useId, useState } from 'react';
import { Button } from '@monksflow/monks-ui';
import { useReducedMotion } from 'motion/react';
import { PlusIcon } from 'lucide-react';
import { microInteraction } from '@/shared/ui/MicroInteractions';
import { useTranslation } from 'react-i18next';

// The picker opens mid-recoil rather than after it finishes, so the
// squish reads as feedback on the click rather than a delay before the picker.
const ADD_PICKER_OPEN_DELAY_MS = 200;

interface EmptyShortcutSlotProps {
  slotIndex: number;
  disabled?: boolean;
  onAdd: () => void;
}

export function EmptyShortcutSlot({ slotIndex, disabled = false, onAdd }: EmptyShortcutSlotProps) {
  const { t } = useTranslation('springboard');
  const shouldReduceMotion = useReducedMotion();
  const [isRecoiling, setIsRecoiling] = useState(false);
  const labelId = useId();

  const handleAdd = () => {
    if (shouldReduceMotion) {
      onAdd();
      return;
    }
    setIsRecoiling(true);
    setTimeout(onAdd, ADD_PICKER_OPEN_DELAY_MS);
  };

  if (disabled) {
    // A locked slot is a bare placeholder, not an affordance: no border, no
    // icon, no visible copy — only the tint that shows a tile is reserved
    // there. It stays a button so the reason it is locked reaches assistive
    // tech.
    return (
      <button
        type="button"
        data-testid={`shortcut-slot-${slotIndex}-empty`}
        disabled
        aria-label={t('dashboard.emptyShortcutSlot.fillThePreviousSlotFirst')}
        className="h-full w-full cursor-default rounded-lg bg-muted/70 outline-none"
      />
    );
  }

  return (
    // The dashed card is a bare wrapper, not the affordance — only the round
    // plus Button below is interactive, so this stays a plain non-clickable
    // container rather than a second (nested) button around it.
    <div
      data-testid={`shortcut-slot-${slotIndex}-empty`}
      className="flex h-full w-full flex-col items-center justify-center gap-sm rounded-lg border border-dashed border-border bg-muted/70 p-md"
    >
      <Button
        variant="default"
        size="icon-sm"
        roundness="full"
        onClick={handleAdd}
        aria-labelledby={labelId}
        className={`group shadow-sm ${
          // `animate-[...]` sets one CSS property, so the mount fade-in and
          // the click recoil can't run as two separate classes at once —
          // recoil wins once clicked (the tile unmounts before it finishes).
          isRecoiling ? microInteraction.shortcutRecoil : microInteraction.shortcutContentIn
        }`}
      >
        <PlusIcon className={`size-4 ${microInteraction.shortcutAddIcon}`} />
      </Button>

      <span
        id={labelId}
        className={`type-body-sm text-muted-foreground ${microInteraction.shortcutContentIn}`}
      >
        {t('dashboard.emptyShortcutSlot.addShortcut')}
      </span>
    </div>
  );
}
